import React, { useEffect, useMemo, useState } from "react";
import ScreenHeader from "../components/layout/ScreenHeader";
import {
  Avatar as CmAvatar,
  Badge,
  Button as CmButton,
  Card as CmCard,
  EmptyState,
  FilterBar,
  IconButton as CmIconButton,
  IconChip,
  KeyValue,
  Modal,
  Notice,
  PersonCell,
  SearchInput,
  SelectInput,
  Skeleton,
  Spinner,
  StatCard as CmStatCard,
  StatGrid,
  StatusPill,
  TableSkeleton,
  TextInput,
  Textarea,
} from "../ui";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertTriangle,
  Armchair,
  Building2,
  ChartNoAxesCombined,
  ChevronDown,
  CirclePlus,
  Download,
  Info,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Power,
  PowerOff,
  QrCode,
  RefreshCw,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  addClubToVenue,
  createVenue,
  deleteClub,
  deleteLocation,
  listVenues,
  resetLocationDelete,
  updateClub,
  updateLocation,
} from "../actions/clubActions";
import TableManagement from "../components/TableManagement";
import {
  CLUB_UPDATE_RESET,
  CLUB_DELETE_RESET,
  LOCATION_UPDATE_RESET,
  VENUE_ADD_CLUB_RESET,
  VENUE_CREATE_RESET,
} from "../constants/clubConstants";
import { useDialog } from '../components/DialogProvider';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const getEmptyVenueForm = () => ({
  name: "",
  street: "",
  city: "",
  cityCode: "",
  state: "",
  zipCode: "",
  country: "India",
  phone: "",
  email: "",
});

// GSTIN is optional, but when given it must be the full 15-character number
const GST_NUMBER_PATTERN = /^[0-9A-Z]{15}$/;
const getGstNumberError = (value) => {
  const gst = String(value || "").trim();
  return gst && !GST_NUMBER_PATTERN.test(gst) ? "GST number must be 15 letters or digits" : "";
};

const PHONE_MAX_DIGITS = 10;

// Keep digits only and cap at 10 so the field can never hold more than a phone number
const sanitizePhone = (value) => String(value || "").replace(/\D/g, "").slice(0, PHONE_MAX_DIGITS);

const getPhoneError = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits && digits.length !== PHONE_MAX_DIGITS
    ? `Phone number must be ${PHONE_MAX_DIGITS} digits`
    : "";
};

const PhoneField = ({ value, onChange, ...props }) => {
  const error = getPhoneError(value);
  return (
    <TextInput
      {...props}
      value={value}
      onChange={(event) => onChange(sanitizePhone(event.target.value))}
      maxLength={PHONE_MAX_DIGITS}
      inputMode="numeric"
      pattern="[0-9]*"
      error={error || undefined}
      help={`${String(value || "").replace(/\D/g, "").length}/${PHONE_MAX_DIGITS} digits`}
    />
  );
};

// Small heading that splits a long form into sections
const FormSection = ({ title, description, children }) => (
  <section className="flex flex-col gap-4">
    <div>
      <h3 className="text-sm font-bold text-cm-text m-0">{title}</h3>
      {description && <p className="text-xs text-cm-muted mt-0.5 mb-0">{description}</p>}
    </div>
    {children}
  </section>
);

// Venue name, address and contact. `showCityCode` is only editable after creation.
const VenueFields = ({ form, onChange, showCityCode = false }) => (
  <div className="grid gap-4 sm:grid-cols-6">
    <TextInput
      className="sm:col-span-6"
      label="Venue name"
      required
      value={form.name}
      onChange={(event) => onChange("name", event.target.value)}
    />
    <TextInput
      className="sm:col-span-6"
      label="Street address"
      value={form.street}
      onChange={(event) => onChange("street", event.target.value)}
    />
    <TextInput
      className={showCityCode ? "sm:col-span-2" : "sm:col-span-3"}
      label="City"
      value={form.city}
      onChange={(event) => onChange("city", event.target.value)}
    />
    {showCityCode && (
      <TextInput
        className="sm:col-span-2"
        label="City code"
        value={form.cityCode}
        onChange={(event) => onChange("cityCode", event.target.value)}
      />
    )}
    <TextInput
      className={showCityCode ? "sm:col-span-2" : "sm:col-span-3"}
      label="State"
      value={form.state}
      onChange={(event) => onChange("state", event.target.value)}
    />
    <TextInput
      className="sm:col-span-3"
      label="Zip code"
      value={form.zipCode}
      onChange={(event) => onChange("zipCode", event.target.value)}
    />
    <TextInput
      className="sm:col-span-3"
      label="Country"
      value={form.country}
      onChange={(event) => onChange("country", event.target.value)}
    />
    <PhoneField
      className="sm:col-span-3"
      label="Phone"
      value={form.phone}
      onChange={(value) => onChange("phone", value)}
    />
    <TextInput
      className="sm:col-span-3"
      label="Email"
      type="email"
      value={form.email}
      onChange={(event) => onChange("email", event.target.value)}
    />
  </div>
);

// Club identity, contact and table settings
const ClubFields = ({ form, onChange, showGst = true, showStatus = false, showRake = true }) => {
  const withRake = showRake && !showStatus;
  const buyInSpan = showStatus ? "sm:col-span-3 md:col-span-2" : withRake ? "sm:col-span-2" : "sm:col-span-3";
  const gstError = showGst ? getGstNumberError(form.gstNumber) : "";
  return (
    <div className="grid gap-4 sm:grid-cols-6">
      <TextInput
        className={showGst ? "sm:col-span-4" : "sm:col-span-6"}
        label="Club name"
        required
        value={form.name}
        onChange={(event) => onChange("name", event.target.value)}
      />
      {showGst && (
        <TextInput
          className="sm:col-span-2"
          label="GST number"
          value={form.gstNumber}
          onChange={(event) => onChange("gstNumber", event.target.value.toUpperCase())}
          maxLength={15}
          error={gstError || undefined}
          help={`Optional · printed on invoices (${String(form.gstNumber || "").trim().length}/15)`}
        />
      )}
      <TextInput
        className="sm:col-span-2"
        label="Contact person"
        value={form.contactPerson}
        onChange={(event) => onChange("contactPerson", event.target.value)}
      />
      <PhoneField
        className="sm:col-span-2"
        label="Phone"
        value={form.phone}
        onChange={(value) => onChange("phone", value)}
      />
      <TextInput
        className="sm:col-span-2"
        label="Email"
        type="email"
        value={form.email}
        onChange={(event) => onChange("email", event.target.value)}
      />
      {showStatus && (
        <SelectInput
          className="sm:col-span-6 md:col-span-2"
          label="Status"
          value={String(form.isActive)}
          onChange={(event) => onChange("isActive", event.target.value === "true")}
          options={[
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
          ]}
        />
      )}
      {withRake && (
        <TextInput
          className="sm:col-span-2"
          label="Rake"
          type="number"
          suffix="%"
          value={form.rakePercentage}
          onChange={(event) => onChange("rakePercentage", event.target.value)}
        />
      )}
      <TextInput
        className={buyInSpan}
        label="Min buy-in"
        type="number"
        prefix="₹"
        value={form.minBuyIn}
        onChange={(event) => onChange("minBuyIn", event.target.value)}
      />
      <TextInput
        className={buyInSpan}
        label="Max buy-in"
        type="number"
        prefix="₹"
        value={form.maxBuyIn}
        onChange={(event) => onChange("maxBuyIn", event.target.value)}
      />
    </div>
  );
};

// Seller block for this club's invoices; blank fields fall back to the invoice template
const ClubBusinessFields = ({ form, onChange }) => (
  <FormSection
    title="Business details"
    description="Printed on this club's invoices. Leave blank to use the invoice template's details."
  >
    <div className="grid gap-4 sm:grid-cols-4">
      <TextInput
        className="sm:col-span-2"
        label="Business name"
        value={form.businessName}
        onChange={(event) => onChange("businessName", event.target.value)}
      />
      <PhoneField
        label="Business phone"
        value={form.businessPhone}
        onChange={(value) => onChange("businessPhone", value)}
      />
      <TextInput
        label="Business email"
        type="email"
        value={form.businessEmail}
        onChange={(event) => onChange("businessEmail", event.target.value)}
      />
      <Textarea
        className="sm:col-span-4"
        label="Business address"
        rows={2}
        value={form.businessAddress}
        onChange={(event) => onChange("businessAddress", event.target.value)}
      />
    </div>
  </FormSection>
);

const getEmptyClubForm = (overrides = {}) => ({
  name: "",
  contactPerson: "",
  phone: "",
  email: "",
  gstNumber: "",
  businessName: "",
  businessAddress: "",
  businessPhone: "",
  businessEmail: "",
  rakePercentage: 5,
  minBuyIn: 100,
  maxBuyIn: 100000,
  isActive: true,
  ...overrides,
});

const getEmptyCreateVenueForm = () => ({
  venue: getEmptyVenueForm(),
  club: getEmptyClubForm(),
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(value) || 0,
  );

const formatAddress = (venue) => {
  const address = venue?.address || {};
  const primary = [address.street, address.city, address.state]
    .filter(Boolean)
    .join(", ");
  const secondary = [address.zipCode, address.country].filter(Boolean).join(", ");

  if (primary && secondary) {
    return `${primary} | ${secondary}`;
  }
  return primary || secondary || "Address not added";
};

const venueMatchesSearch = (venue, query) => {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  const venueText = [
    venue.name,
    venue.address?.street,
    venue.address?.city,
    venue.address?.state,
    venue.address?.zipCode,
    venue.address?.country,
    venue.contactInfo?.phone,
    venue.contactInfo?.email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (venueText.includes(normalizedQuery)) {
    return true;
  }

  return (venue.clubs || []).some((club) =>
    [
      club.name,
      club.clubCode,
      club.contactPerson,
      club.phone,
      club.email,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedQuery),
  );
};

const getAbsoluteUrl = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "";
    }

    const normalizedPath = parsed.pathname.replace(/\/+$/, "");
    return `${parsed.origin}${normalizedPath}`;
  } catch (_error) {
    return "";
  }
};

const resolvePlayerBaseUrl = () => {
  const publicUrl = getAbsoluteUrl(process.env.REACT_APP_PUBLIC_URL);
  if (publicUrl) {
    return publicUrl;
  }

  const browserOrigin = getAbsoluteUrl(window.location.origin);
  if (browserOrigin) {
    try {
      const parsedOrigin = new URL(browserOrigin);
      if (parsedOrigin.port === "3000") {
        parsedOrigin.port = "5000";
        return getAbsoluteUrl(parsedOrigin.toString());
      }
    } catch (_error) {
      return browserOrigin;
    }

    return browserOrigin;
  }

  const backendTarget = getAbsoluteUrl(process.env.REACT_APP_BACKEND_TARGET);
  if (backendTarget) {
    return backendTarget;
  }

  const apiUrl = getAbsoluteUrl(process.env.REACT_APP_API_URL);
  if (apiUrl) {
    return apiUrl.replace(/\/api\/?$/i, "");
  }

  return "";
};

const mapVenueToForm = (venue) => ({
  name: venue?.name || "",
  street: venue?.address?.street || "",
  city: venue?.address?.city || "",
  cityCode: venue?.address?.cityCode || "",
  state: venue?.address?.state || "",
  zipCode: venue?.address?.zipCode || "",
  country: venue?.address?.country || "India",
  phone: venue?.contactInfo?.phone || "",
  email: venue?.contactInfo?.email || "",
});

const mapClubToForm = (club, venue = null) =>
  getEmptyClubForm({
    name: club?.name || "",
    contactPerson: club?.contactPerson || "",
    phone: club?.phone || venue?.contactInfo?.phone || "",
    email: club?.email || venue?.contactInfo?.email || "",
    gstNumber: club?.gstNumber || "",
    businessName: club?.businessDetails?.name || "",
    businessAddress: club?.businessDetails?.address || "",
    businessPhone: club?.businessDetails?.phone || "",
    businessEmail: club?.businessDetails?.email || "",
    rakePercentage: club?.settings?.rakePercentage ?? 5,
    minBuyIn: club?.settings?.minBuyIn ?? 100,
    maxBuyIn: club?.settings?.maxBuyIn ?? 100000,
    isActive: club?.isActive ?? true,
  });

const buildVenuePayload = (form) => ({
  location: {
    name: form.venue.name.trim(),
    address: {
      street: form.venue.street.trim(),
      city: form.venue.city.trim(),
      cityCode: form.venue.cityCode.trim(),
      state: form.venue.state.trim(),
      zipCode: form.venue.zipCode.trim(),
      country: form.venue.country.trim() || "India",
    },
    contactInfo: {
      phone: form.venue.phone.trim(),
      email: form.venue.email.trim(),
    },
  },
  primaryClub: {
    name: form.club.name.trim(),
    contactPerson: form.club.contactPerson.trim(),
    phone: form.club.phone.trim(),
    email: form.club.email.trim(),
    settings: {
      rakePercentage: Number(form.club.rakePercentage) || 0,
      minBuyIn: Number(form.club.minBuyIn) || 0,
      maxBuyIn: Number(form.club.maxBuyIn) || 0,
    },
    isActive: form.club.isActive,
  },
});

const buildClubPayload = (form) => ({
  name: form.name.trim(),
  contactPerson: form.contactPerson.trim(),
  phone: form.phone.trim(),
  email: form.email.trim(),
  gstNumber: form.gstNumber.trim(),
  businessDetails: {
    name: form.businessName.trim(),
    address: form.businessAddress.trim(),
    phone: form.businessPhone.trim(),
    email: form.businessEmail.trim(),
  },
  settings: {
    rakePercentage: Number(form.rakePercentage) || 0,
    minBuyIn: Number(form.minBuyIn) || 0,
    maxBuyIn: Number(form.maxBuyIn) || 0,
  },
  isActive: form.isActive,
});

const buildVenueUpdatePayload = (form) => ({
  name: form.name.trim(),
  address: {
    street: form.street.trim(),
    city: form.city.trim(),
    cityCode: form.cityCode.trim(),
    state: form.state.trim(),
    zipCode: form.zipCode.trim(),
    country: form.country.trim() || "India",
  },
  contactInfo: {
    phone: form.phone.trim(),
    email: form.email.trim(),
  },
});

const getAuthConfig = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

const StatCard = ({ title, value, subtitle }) => (
  <CmStatCard label={title} value={value} hint={subtitle} className="h-full" />
);

const Venues = () => {
  const { confirm } = useDialog();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedVenueIds, setExpandedVenueIds] = useState({});

  const [createVenueOpen, setCreateVenueOpen] = useState(false);
  const [createVenueForm, setCreateVenueForm] = useState(getEmptyCreateVenueForm);
  const [clubPrefillTouched, setClubPrefillTouched] = useState({
    phone: false,
    email: false,
  });

  const [editVenueOpen, setEditVenueOpen] = useState(false);
  const [editVenueForm, setEditVenueForm] = useState(getEmptyVenueForm);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const [addClubOpen, setAddClubOpen] = useState(false);
  const [editClubOpen, setEditClubOpen] = useState(false);
  const [clubForm, setClubForm] = useState(getEmptyClubForm);
  const [selectedClub, setSelectedClub] = useState(null);

  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);

  const [tableManagerOpen, setTableManagerOpen] = useState(false);
  const [selectedClubForTables, setSelectedClubForTables] = useState(null);

  const [statsOpen, setStatsOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [clubStats, setClubStats] = useState(null);

  const [staffOpen, setStaffOpen] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [assignedAdmins, setAssignedAdmins] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [staffActionLoading, setStaffActionLoading] = useState(false);

  const venueList = useSelector((state) => state.venueList);
  const { loading, venues = [], meta = {}, error } = venueList;

  const venueCreate = useSelector((state) => state.venueCreate);
  const {
    loading: loadingVenueCreate,
    success: successVenueCreate,
    error: errorVenueCreate,
  } = venueCreate;

  const venueAddClub = useSelector((state) => state.venueAddClub);
  const {
    loading: loadingVenueAddClub,
    success: successVenueAddClub,
    error: errorVenueAddClub,
  } = venueAddClub;

  const locationUpdateState = useSelector((state) => state.locationUpdate);
  const {
    loading: loadingVenueUpdate,
    success: successVenueUpdate,
    error: errorVenueUpdate,
    message: venueUpdateMessage,
  } = locationUpdateState;

  const locationDeleteState = useSelector((state) => state.locationDelete);
  const {
    loading: loadingVenueDelete,
    success: successVenueDelete,
    error: errorVenueDelete,
    data: venueDeleteResponse,
  } = locationDeleteState;

  const clubUpdateState = useSelector((state) => state.clubUpdate);
  const {
    loading: loadingClubUpdate,
    success: successClubUpdate,
    error: errorClubUpdate,
    message: clubUpdateMessage,
  } = clubUpdateState;

  const clubDeleteState = useSelector((state) => state.clubDelete);
  const {
    loading: loadingClubDelete,
    success: successClubDelete,
    error: errorClubDelete,
    data: clubDeleteResponse,
  } = clubDeleteState;

  const adminLogin = useSelector((state) => state.adminLogin);
  const { userInfo: adminInfo } = adminLogin || {};

  const getRoleInfo = useSelector((state) => state.getRoleInfo);
  const resolvedRole = getRoleInfo?.getRoleData || adminInfo?.role || {};

  const isSuperAdmin = resolvedRole?.isSuperAdmin === true;
  const canViewVenues =
    isSuperAdmin ||
    resolvedRole?.locationsView === true ||
    resolvedRole?.clubsView === true;
  const canViewClubs = isSuperAdmin || resolvedRole?.clubsView === true;
  const canCreateVenue =
    isSuperAdmin ||
    (resolvedRole?.locationsAdd === true && resolvedRole?.clubsAdd === true);
  const canEditVenue = isSuperAdmin || resolvedRole?.locationsUpdate === true;
  const canDeleteVenue = isSuperAdmin;
  const canAddClub = isSuperAdmin || resolvedRole?.clubsAdd === true;
  const canEditClub = isSuperAdmin || resolvedRole?.clubsUpdate === true;
  const canDeleteClub = isSuperAdmin || resolvedRole?.clubsDelete === true;
  const canAssignStaff = isSuperAdmin || resolvedRole?.adminClubAssignment === true;
  const canViewAdmins = isSuperAdmin || resolvedRole?.usersView === true;

  const filteredVenues = useMemo(
    () => venues.filter((venue) => venueMatchesSearch(venue, searchQuery)),
    [venues, searchQuery],
  );

  const summaryStats = useMemo(
    () => ({
      totalVenues: venues.length,
      activeVenues: venues.filter((venue) => venue.isActive).length,
      totalClubs: venues.reduce(
        (sum, venue) => sum + (Number(venue.clubCount) || 0),
        0,
      ),
      activeClubs: venues.reduce(
        (sum, venue) => sum + (Number(venue.activeClubCount) || 0),
        0,
      ),
    }),
    [venues],
  );

  useEffect(() => {
    if (canViewVenues) {
      dispatch(listVenues());
    }
  }, [
    dispatch,
    canViewVenues,
    successVenueCreate,
    successVenueAddClub,
    successVenueUpdate,
    successVenueDelete,
    successClubUpdate,
    successClubDelete,
  ]);

  useEffect(() => {
    if (successVenueCreate) {
      toast.success("Venue created successfully");
      setCreateVenueOpen(false);
      setCreateVenueForm(getEmptyCreateVenueForm());
      setClubPrefillTouched({ phone: false, email: false });
      dispatch({ type: VENUE_CREATE_RESET });
    }
    if (errorVenueCreate) {
      toast.error(errorVenueCreate);
    }
  }, [successVenueCreate, errorVenueCreate, dispatch]);

  useEffect(() => {
    if (successVenueAddClub) {
      toast.success("Club added to venue");
      setAddClubOpen(false);
      setSelectedVenue(null);
      setClubForm(getEmptyClubForm());
      dispatch({ type: VENUE_ADD_CLUB_RESET });
    }
    if (errorVenueAddClub) {
      toast.error(errorVenueAddClub);
    }
  }, [successVenueAddClub, errorVenueAddClub, dispatch]);

  useEffect(() => {
    if (successVenueUpdate) {
      toast.success(
        venueUpdateMessage && venueUpdateMessage !== "Location updated successfully"
          ? venueUpdateMessage
          : "Venue updated successfully",
      );
      setEditVenueOpen(false);
      setSelectedVenue(null);
      dispatch({ type: LOCATION_UPDATE_RESET });
    }
    if (errorVenueUpdate) {
      toast.error(errorVenueUpdate);
    }
  }, [successVenueUpdate, errorVenueUpdate, venueUpdateMessage, dispatch]);

  useEffect(() => {
    if (successClubUpdate) {
      toast.success(clubUpdateMessage || "Club updated successfully");
      setEditClubOpen(false);
      setSelectedClub(null);
      dispatch({ type: CLUB_UPDATE_RESET });
    }
    if (errorClubUpdate) {
      toast.error(errorClubUpdate);
    }
  }, [successClubUpdate, errorClubUpdate, clubUpdateMessage, dispatch]);

  useEffect(() => {
    if (successVenueDelete) {
      toast.success(venueDeleteResponse?.message || "Venue updated successfully");
      dispatch(resetLocationDelete());
    }
    if (errorVenueDelete) {
      toast.error(errorVenueDelete);
      dispatch(resetLocationDelete());
    }
  }, [successVenueDelete, errorVenueDelete, venueDeleteResponse, dispatch]);

  useEffect(() => {
    if (successClubDelete) {
      toast.success(clubDeleteResponse?.message || "Club deactivated successfully");
      dispatch({ type: CLUB_DELETE_RESET });
    }
    if (errorClubDelete) {
      toast.error(errorClubDelete);
      dispatch({ type: CLUB_DELETE_RESET });
    }
  }, [successClubDelete, errorClubDelete, clubDeleteResponse, dispatch]);

  const handleRefresh = () => {
    dispatch(listVenues());
  };

  const handleToggleVenue = (venueId) => (_event, expanded) => {
    setExpandedVenueIds((prev) => ({
      ...prev,
      [venueId]: expanded,
    }));
  };

  const openCreateVenueDialog = () => {
    setCreateVenueForm(getEmptyCreateVenueForm());
    setClubPrefillTouched({ phone: false, email: false });
    setCreateVenueOpen(true);
  };

  const handleCreateVenueVenueChange = (field, value) => {
    setCreateVenueForm((prev) => {
      const next = {
        ...prev,
        venue: {
          ...prev.venue,
          [field]: value,
        },
      };

      if (field === "phone" && !clubPrefillTouched.phone) {
        next.club = { ...next.club, phone: value };
      }

      if (field === "email" && !clubPrefillTouched.email) {
        next.club = { ...next.club, email: value };
      }

      return next;
    });
  };

  const handleCreateVenueClubChange = (field, value) => {
    setCreateVenueForm((prev) => ({
      ...prev,
      club: {
        ...prev.club,
        [field]: value,
      },
    }));

    if (field === "phone" || field === "email") {
      setClubPrefillTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    }
  };

  const handleSubmitCreateVenue = () => {
    if (!createVenueForm.venue.name.trim()) {
      toast.error("Venue name is required");
      return;
    }

    if (!createVenueForm.club.name.trim()) {
      toast.error("First club name is required");
      return;
    }

    const createPhoneError =
      getPhoneError(createVenueForm.venue.phone) ||
      getPhoneError(createVenueForm.club.phone);
    if (createPhoneError) {
      toast.error(createPhoneError);
      return;
    }

    dispatch(createVenue(buildVenuePayload(createVenueForm)));
  };

  const openEditVenueDialog = (venue) => {
    setSelectedVenue(venue);
    setEditVenueForm(mapVenueToForm(venue));
    setEditVenueOpen(true);
  };

  const handleEditVenueChange = (field, value) => {
    setEditVenueForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitEditVenue = () => {
    if (!selectedVenue?._id) {
      return;
    }

    if (!editVenueForm.name.trim()) {
      toast.error("Venue name is required");
      return;
    }

    if (getPhoneError(editVenueForm.phone)) {
      toast.error(getPhoneError(editVenueForm.phone));
      return;
    }

    dispatch(updateLocation(selectedVenue._id, buildVenueUpdatePayload(editVenueForm)));
  };

  const handleDeactivateVenue = async (venue) => {
    if (!canEditVenue) {
      return;
    }

    const message =
      venue.activeClubCount > 0
        ? `Deactivate venue "${venue.name}"? Its ${venue.activeClubCount} active club(s) will also be deactivated.`
        : `Deactivate venue "${venue.name}"?`;

    if (await confirm({ title: 'Deactivate venue', message, confirmText: 'Deactivate', danger: true })) {
      dispatch(deleteLocation(venue._id));
    }
  };

  const handleReactivateVenue = async (venue) => {
    if (!canEditVenue) {
      return;
    }

    const inactiveClubs = (venue.clubCount || 0) - (venue.activeClubCount || 0);
    const message =
      inactiveClubs > 0
        ? `Reactivate venue "${venue.name}"? Its ${inactiveClubs} inactive club(s) will also be reactivated.`
        : `Reactivate venue "${venue.name}"?`;

    if (await confirm({ title: 'Reactivate venue', message, confirmText: 'Reactivate' })) {
      dispatch(updateLocation(venue._id, { isActive: true, reactivateClubs: true }));
    }
  };

  const handleHardDeleteVenue = async (venue) => {
    if (!canDeleteVenue) {
      return;
    }

    const confirmed = await confirm({
      title: 'Delete venue permanently',
      message:
        venue.clubCount > 0
          ? `WARNING: This will permanently delete venue "${venue.name}" along with its ${venue.clubCount} club(s) and ALL their data (transactions, tables, sessions, etc.). This cannot be undone.`
          : `WARNING: This will permanently delete venue "${venue.name}". This cannot be undone.`,
      confirmText: 'Delete permanently',
      danger: true,
    });

    if (confirmed) {
      dispatch(deleteLocation(venue._id, { hardDelete: true }));
    }
  };

  const openAddClubDialog = (venue) => {
    setSelectedVenue(venue);
    setSelectedClub(null);
    setClubForm(
      getEmptyClubForm({
        phone: venue?.contactInfo?.phone || "",
        email: venue?.contactInfo?.email || "",
      }),
    );
    setAddClubOpen(true);
  };

  const openEditClubDialog = (club, venue) => {
    setSelectedVenue(venue);
    setSelectedClub(club);
    setClubForm(mapClubToForm(club, venue));
    setEditClubOpen(true);
  };

  const handleClubFormChange = (field, value) => {
    setClubForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitAddClub = () => {
    if (!selectedVenue?._id) {
      return;
    }

    if (!clubForm.name.trim()) {
      toast.error("Club name is required");
      return;
    }

    if (getGstNumberError(clubForm.gstNumber)) {
      toast.error(getGstNumberError(clubForm.gstNumber));
      return;
    }
    if (getPhoneError(clubForm.phone) || getPhoneError(clubForm.businessPhone)) {
      toast.error(getPhoneError(clubForm.phone) || getPhoneError(clubForm.businessPhone));
      return;
    }
    dispatch(addClubToVenue(selectedVenue._id, buildClubPayload(clubForm)));
  };

  const handleSubmitEditClub = () => {
    if (!selectedClub?._id) {
      return;
    }

    if (!clubForm.name.trim()) {
      toast.error("Club name is required");
      return;
    }
    if (getGstNumberError(clubForm.gstNumber)) {
      toast.error(getGstNumberError(clubForm.gstNumber));
      return;
    }
    if (getPhoneError(clubForm.phone) || getPhoneError(clubForm.businessPhone)) {
      toast.error(getPhoneError(clubForm.phone) || getPhoneError(clubForm.businessPhone));
      return;
    }

    dispatch(updateClub(selectedClub._id, buildClubPayload(clubForm)));
  };

  const handleDeactivateClub = async (club) => {
    if (!canEditClub) {
      return;
    }

    if (await confirm({ title: 'Deactivate club', message: `Deactivate club "${club.name}"?`, confirmText: 'Deactivate', danger: true })) {
      dispatch(deleteClub(club._id));
    }
  };

  const handleReactivateClub = async (club, venue) => {
    if (!canEditClub) {
      return;
    }

    if (!venue?.isActive) {
      toast.error("Reactivate the venue first");
      return;
    }

    if (await confirm({ title: 'Reactivate club', message: `Reactivate club "${club.name}"?`, confirmText: 'Reactivate' })) {
      dispatch(updateClub(club._id, { isActive: true }));
    }
  };

  const handleHardDeleteClub = async (club) => {
    if (!canDeleteClub) {
      return;
    }

    const firstConfirm = await confirm({
      title: 'Permanently delete club',
      message: `WARNING: This will permanently delete club "${club.name}" and ALL its data (transactions, tables, sessions, etc.). This action CANNOT be undone.\n\nAre you sure you want to proceed?`,
      confirmText: 'Continue',
      danger: true,
    });
    if (!firstConfirm) return;

    const secondConfirm = await confirm({
      title: 'Final confirmation',
      message: `Permanently delete "${club.name}" and all associated data? There is no recovery after this.`,
      confirmText: 'Delete permanently',
      danger: true,
    });
    if (!secondConfirm) return;

    dispatch(deleteClub(club._id, { hardDelete: true }));
  };

  const handleOpenQr = async (club) => {
    setSelectedClub(club);
    setQrLoading(true);
    try {
      const playerBaseUrl = resolvePlayerBaseUrl();
      const requestPayload = playerBaseUrl ? { baseUrl: playerBaseUrl } : {};
      const response = await axios.post(
        `${API_URL}/clubs/${club._id}/generate-qr`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${adminInfo?.token}`,
          },
        },
      );

      setQrCodeData({
        ...response.data?.data,
        clubName: club.name,
        clubCode: club.clubCode,
      });
      setQrOpen(true);
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.message || "Failed to generate QR code",
      );
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrCodeData?.qrCode) {
      return;
    }

    const link = document.createElement("a");
    const clubCode = String(qrCodeData.clubCode || "club-qr")
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "-");
    link.href = qrCodeData.qrCode;
    link.download = `${clubCode}.png`;
    link.click();
  };

  const handleOpenTables = (club) => {
    setSelectedClubForTables(club);
    setTableManagerOpen(true);
  };

  const handleOpenStats = async (club) => {
    setSelectedClub(club);
    setStatsOpen(true);
    setStatsLoading(true);
    setClubStats(null);
    try {
      const response = await axios.get(
        `${API_URL}/clubs/${club._id}/stats`,
        getAuthConfig(adminInfo?.token),
      );
      setClubStats(response.data?.data || null);
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || "Failed to load club stats");
    } finally {
      setStatsLoading(false);
    }
  };

  const loadStaffData = async (clubId) => {
    setStaffLoading(true);
    try {
      const clubAdminsPromise = axios.get(
        `${API_URL}/clubs/${clubId}/admins`,
        getAuthConfig(adminInfo?.token),
      );

      const adminsPromise = canViewAdmins
        ? axios.get(`${API_URL}/admins`, getAuthConfig(adminInfo?.token))
        : Promise.resolve({ data: [] });

      const [clubAdminsResponse, adminsResponse] = await Promise.all([
        clubAdminsPromise,
        adminsPromise,
      ]);

      const assigned = clubAdminsResponse.data?.data || [];
      const allAdmins = Array.isArray(adminsResponse.data)
        ? adminsResponse.data
        : adminsResponse.data?.users || [];

      setAssignedAdmins(assigned);
      setAvailableAdmins(allAdmins);
      setSelectedAdminId("");
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || "Failed to load club staff");
      setAssignedAdmins([]);
      setAvailableAdmins([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleOpenStaff = async (club) => {
    setSelectedClub(club);
    setStaffOpen(true);
    await loadStaffData(club._id);
  };

  const unassignedAdminOptions = useMemo(() => {
    const assignedIds = new Set(assignedAdmins.map((admin) => admin._id));
    return availableAdmins.filter((admin) => !assignedIds.has(admin._id));
  }, [availableAdmins, assignedAdmins]);

  const handleAssignAdmin = async () => {
    if (!selectedClub?._id || !selectedAdminId) {
      return;
    }

    setStaffActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/clubs/${selectedClub._id}/assign-admin`,
        { adminId: selectedAdminId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminInfo?.token}`,
          },
        },
      );

      toast.success(response.data?.message || "Admin assigned");
      await loadStaffData(selectedClub._id);
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || "Failed to assign admin");
    } finally {
      setStaffActionLoading(false);
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    if (!selectedClub?._id) {
      return;
    }

    setStaffActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/clubs/${selectedClub._id}/remove-admin`,
        { adminId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminInfo?.token}`,
          },
        },
      );

      toast.success(response.data?.message || "Admin removed");
      await loadStaffData(selectedClub._id);
    } catch (requestError) {
      toast.error(requestError.response?.data?.message || "Failed to remove admin");
    } finally {
      setStaffActionLoading(false);
    }
  };

  if (!canViewVenues) {
    return (
      <Notice tone="danger" icon={AlertTriangle}>
        You do not have permission to view venue management.
      </Notice>
    );
  }

  return (
    <div>
      <ScreenHeader
        title="Venues"
        description="Each venue is a physical location with one or more clubs. Add a venue and its first club together, then grow from there."
        actions={
          <>
            <CmButton variant="secondary" icon={RefreshCw} onClick={handleRefresh}>Refresh</CmButton>
            {canCreateVenue && (
              <CmButton icon={CirclePlus} onClick={openCreateVenueDialog}>Add venue</CmButton>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-5 mb-5">
        <StatGrid>
          <StatCard
            title="Total venues"
            value={formatNumber(summaryStats.totalVenues)}
            subtitle="Physical locations tracked"
          />
          <StatCard
            title="Active venues"
            value={formatNumber(summaryStats.activeVenues)}
            subtitle="Ready for daily operations"
          />
          <StatCard
            title="Total clubs"
            value={canViewClubs ? formatNumber(summaryStats.totalClubs) : "—"}
            subtitle={canViewClubs ? "Operational clubs under venues" : "Club visibility restricted"}
          />
          <StatCard
            title="Active clubs"
            value={canViewClubs ? formatNumber(summaryStats.activeClubs) : "—"}
            subtitle={canViewClubs ? "Live clubs today" : "Club visibility restricted"}
          />
        </StatGrid>

        <FilterBar
          actions={
            <span className="text-[13px] text-cm-muted">
              Showing {filteredVenues.length} of {venues.length} venues
              {meta?.canViewClubs === false ? " · clubs hidden by role permissions" : ""}
            </span>
          }
        >
          <SearchInput
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            placeholder="Search venue, city, club name, code or contact…"
            className="w-full sm:w-96"
          />
        </FilterBar>

        {error && (
          <Notice tone="danger" icon={AlertTriangle}>
            {error}
          </Notice>
        )}

        {loading ? (
          <CmCard padded={false}>
            <TableSkeleton rows={4} cols={4} />
          </CmCard>
        ) : filteredVenues.length === 0 ? (
          <CmCard>
            <EmptyState
              icon={MapPin}
              title="No venues match this view"
              description="Start with a venue and its first club. Both are created in one step."
              action={
                canCreateVenue && (
                  <CmButton icon={CirclePlus} onClick={openCreateVenueDialog}>
                    Create first venue
                  </CmButton>
                )
              }
            />
          </CmCard>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredVenues.map((venue) => {
              const expanded = Boolean(expandedVenueIds[venue._id]);
              const clubsList = venue.clubs || [];
              const toggle = () => handleToggleVenue(venue._id)(null, !expanded);
              return (
                <CmCard key={venue._id} padded={false} className="overflow-hidden">
                  {/* Venue header */}
                  <div className="flex flex-col gap-3 p-4 sm:px-5 lg:flex-row lg:items-center">
                    <button
                      type="button"
                      onClick={toggle}
                      aria-expanded={expanded}
                      aria-controls={`venue-panel-${venue._id}`}
                      className="flex flex-1 items-center gap-3 min-w-0 text-left"
                    >
                      <IconChip icon={MapPin} tone={venue.isActive ? "gold" : "neutral"} size={40} />
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-base font-bold text-cm-text truncate">{venue.name}</span>
                          <StatusPill status={venue.isActive ? "Active" : "Inactive"} size="sm" />
                        </span>
                        <span className="block text-[13px] text-cm-muted truncate">{formatAddress(venue)}</span>
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-cm-muted lg:justify-end">
                      <span className="inline-flex items-center gap-1.5">
                        <Phone size={13} aria-hidden="true" />
                        {venue.contactInfo?.phone || "No phone"}
                      </span>
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <Mail size={13} aria-hidden="true" />
                        <span className="truncate max-w-[200px]">{venue.contactInfo?.email || "No email"}</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Badge tone="gold">
                          {venue.clubCount || 0} club{venue.clubCount === 1 ? "" : "s"}
                        </Badge>
                        <Badge tone={venue.activeClubCount ? "success" : "neutral"}>
                          {venue.activeClubCount || 0} active
                        </Badge>
                      </span>
                    </div>

                    <div className="flex items-center gap-1 lg:ml-2">
                      {canAddClub && venue.isActive && (
                        <CmIconButton
                          icon={CirclePlus}
                          variant="ghost"
                          size="sm"
                          label={venue.clubCount > 0 ? "Add another club" : "Add first club"}
                          onClick={() => openAddClubDialog(venue)}
                        />
                      )}
                      {canEditVenue && (
                        <CmIconButton
                          icon={Pencil}
                          variant="ghost"
                          size="sm"
                          label="Edit venue"
                          onClick={() => openEditVenueDialog(venue)}
                        />
                      )}
                      {canEditVenue && venue.isActive && (
                        <CmIconButton
                          icon={PowerOff}
                          variant="ghost"
                          size="sm"
                          label="Deactivate venue"
                          disabled={loadingVenueDelete}
                          onClick={() => handleDeactivateVenue(venue)}
                        />
                      )}
                      {canEditVenue && !venue.isActive && (
                        <CmIconButton
                          icon={Power}
                          variant="ghost"
                          size="sm"
                          label="Reactivate venue"
                          disabled={loadingVenueUpdate}
                          onClick={() => handleReactivateVenue(venue)}
                        />
                      )}
                      {canDeleteVenue && (
                        <CmIconButton
                          icon={Trash2}
                          variant="ghost"
                          size="sm"
                          label="Permanently delete venue"
                          className="hover:!text-cm-danger"
                          disabled={loadingVenueDelete}
                          onClick={() => handleHardDeleteVenue(venue)}
                        />
                      )}
                      <CmIconButton
                        icon={ChevronDown}
                        variant="secondary"
                        size="sm"
                        label={expanded ? "Hide clubs" : "Show clubs"}
                        aria-expanded={expanded}
                        aria-controls={`venue-panel-${venue._id}`}
                        className={`ml-1 transition-transform ${expanded ? "rotate-180" : ""}`}
                        onClick={toggle}
                      />
                    </div>
                  </div>

                  {/* Venue detail: clubs */}
                  {expanded && (
                    <div
                      id={`venue-panel-${venue._id}`}
                      className="border-t border-cm-border bg-cm-surface-2 p-4 sm:p-5"
                    >
                      {clubsList.length === 0 ? (
                        <EmptyState
                          compact
                          icon={Building2}
                          title="No clubs at this venue yet"
                          description="Add the first club here to start running tables."
                          action={
                            canAddClub && venue.isActive && (
                              <CmButton size="sm" icon={CirclePlus} onClick={() => openAddClubDialog(venue)}>
                                Add first club
                              </CmButton>
                            )
                          }
                        />
                      ) : (
                        <ul className="grid gap-3 m-0 p-0 list-none lg:grid-cols-2">
                          {clubsList.map((club) => (
                            <li
                              key={club._id}
                              className="flex flex-col rounded-cm-lg border border-cm-border bg-cm-surface min-w-0"
                            >
                              <div className="flex items-start gap-3 p-4 pb-3">
                                <CmAvatar name={club.name} size={40} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-cm-text m-0 truncate">{club.name}</p>
                                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                    <Badge>
                                      <span className="font-mono">{club.clubCode || "No code"}</span>
                                    </Badge>
                                    <StatusPill status={club.isActive ? "Active" : "Inactive"} size="sm" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5 -mr-1 -mt-1">
                                  {canEditClub && (
                                    <CmIconButton
                                      icon={Pencil}
                                      variant="ghost"
                                      size="sm"
                                      label="Edit club"
                                      onClick={() => openEditClubDialog(club, venue)}
                                    />
                                  )}
                                  {canEditClub && club.isActive && (
                                    <CmIconButton
                                      icon={PowerOff}
                                      variant="ghost"
                                      size="sm"
                                      label="Deactivate club"
                                      disabled={loadingClubDelete}
                                      onClick={() => handleDeactivateClub(club)}
                                    />
                                  )}
                                  {canEditClub && !club.isActive && (
                                    <CmIconButton
                                      icon={Power}
                                      variant="ghost"
                                      size="sm"
                                      label="Reactivate club"
                                      disabled={loadingClubUpdate}
                                      onClick={() => handleReactivateClub(club, venue)}
                                    />
                                  )}
                                  {canDeleteClub && (
                                    <CmIconButton
                                      icon={Trash2}
                                      variant="ghost"
                                      size="sm"
                                      label="Permanently delete club and all data"
                                      className="hover:!text-cm-danger"
                                      onClick={() => handleHardDeleteClub(club)}
                                    />
                                  )}
                                </div>
                              </div>

                              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 m-0 px-4 pb-4">
                                <KeyValue label="Contact person">{club.contactPerson || "Not assigned"}</KeyValue>
                                <KeyValue label="Phone">{club.phone || "—"}</KeyValue>
                                <KeyValue label="Email">{club.email || "—"}</KeyValue>
                                <KeyValue label="Rake">{club.settings?.rakePercentage ?? 0}%</KeyValue>
                                <KeyValue label="Buy-in range" className="sm:col-span-2">
                                  {formatCurrency(club.settings?.minBuyIn)} – {formatCurrency(club.settings?.maxBuyIn)}
                                </KeyValue>
                              </dl>

                              <div className="mt-auto flex flex-wrap gap-2 border-t border-cm-border px-4 py-3">
                                <CmButton
                                  variant="secondary"
                                  size="xs"
                                  icon={QrCode}
                                  disabled={qrLoading}
                                  onClick={() => handleOpenQr(club)}
                                >
                                  QR
                                </CmButton>
                                <CmButton variant="secondary" size="xs" icon={Armchair} onClick={() => handleOpenTables(club)}>
                                  Tables
                                </CmButton>
                                <CmButton variant="secondary" size="xs" icon={ChartNoAxesCombined} onClick={() => handleOpenStats(club)}>
                                  Stats
                                </CmButton>
                                <CmButton variant="secondary" size="xs" icon={Users} onClick={() => handleOpenStaff(club)}>
                                  Staff
                                </CmButton>
                              </div>
                            </li>
                          ))}
                          {canAddClub && venue.isActive && (
                            <li>
                              <button
                                type="button"
                                onClick={() => openAddClubDialog(venue)}
                                className="flex h-full min-h-[120px] w-full flex-col items-center justify-center gap-2 rounded-cm-lg border border-dashed border-cm-border-strong text-[13px] font-semibold text-cm-muted hover:border-cm-gold hover:text-cm-gold-text transition-colors"
                              >
                                <CirclePlus size={20} aria-hidden="true" />
                                Add another club
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </CmCard>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={createVenueOpen}
        onClose={() => setCreateVenueOpen(false)}
        title="Create venue"
        description="Add the venue and its first club in one step."
        icon={MapPin}
        size="lg"
        footer={
          <>
            <CmButton variant="secondary" onClick={() => setCreateVenueOpen(false)}>Cancel</CmButton>
            <CmButton loading={loadingVenueCreate} onClick={handleSubmitCreateVenue}>
              Create venue
            </CmButton>
          </>
        }
      >
        <div className="flex flex-col gap-6">
          <FormSection title="Venue details">
            <VenueFields form={createVenueForm.venue} onChange={handleCreateVenueVenueChange} />
          </FormSection>
          <hr className="border-cm-border m-0" />
          <FormSection title="First club" description="You can add more clubs to this venue later.">
            <ClubFields form={createVenueForm.club} onChange={handleCreateVenueClubChange} showGst={false} showRake={false} />
          </FormSection>
        </div>
      </Modal>

      <Modal
        open={editVenueOpen}
        onClose={() => setEditVenueOpen(false)}
        title="Edit venue"
        description={selectedVenue?.name}
        icon={Pencil}
        size="lg"
        footer={
          <>
            <CmButton variant="secondary" onClick={() => setEditVenueOpen(false)}>Cancel</CmButton>
            <CmButton loading={loadingVenueUpdate} onClick={handleSubmitEditVenue}>
              Save venue
            </CmButton>
          </>
        }
      >
        <VenueFields form={editVenueForm} onChange={handleEditVenueChange} showCityCode />
      </Modal>

      <Modal
        open={addClubOpen}
        onClose={() => setAddClubOpen(false)}
        title="Add club"
        description={selectedVenue ? `New club at ${selectedVenue.name}` : undefined}
        icon={Building2}
        size="lg"
        footer={
          <>
            <CmButton variant="secondary" onClick={() => setAddClubOpen(false)}>Cancel</CmButton>
            <CmButton loading={loadingVenueAddClub} onClick={handleSubmitAddClub}>
              Add club
            </CmButton>
          </>
        }
      >
        <div className="flex flex-col gap-6">
          <ClubFields form={clubForm} onChange={handleClubFormChange} />
          <hr className="border-cm-border m-0" />
          <ClubBusinessFields form={clubForm} onChange={handleClubFormChange} />
        </div>
      </Modal>

      <Modal
        open={editClubOpen}
        onClose={() => setEditClubOpen(false)}
        title="Edit club"
        description={selectedClub?.name}
        icon={Pencil}
        size="lg"
        footer={
          <>
            <CmButton variant="secondary" onClick={() => setEditClubOpen(false)}>Cancel</CmButton>
            <CmButton loading={loadingClubUpdate} onClick={handleSubmitEditClub}>
              Save club
            </CmButton>
          </>
        }
      >
        <div className="flex flex-col gap-6">
          <ClubFields form={clubForm} onChange={handleClubFormChange} showStatus />
          <hr className="border-cm-border m-0" />
          <ClubBusinessFields form={clubForm} onChange={handleClubFormChange} />
        </div>
      </Modal>

      <Modal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        title="Club QR code"
        description="For club-specific player entry and onboarding."
        icon={QrCode}
        size="sm"
        footer={
          <>
            <CmButton variant="secondary" onClick={() => setQrOpen(false)}>Close</CmButton>
            <CmButton icon={Download} onClick={handleDownloadQr} disabled={!qrCodeData?.qrCode}>
              Download
            </CmButton>
          </>
        }
      >
        {!qrCodeData ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <Skeleton className="w-[220px] h-[220px] rounded-cm-lg" />
            <Skeleton className="w-32 h-4" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="rounded-cm-lg bg-white p-3 border border-cm-border">
              <img
                src={qrCodeData.qrCode}
                alt={`QR code for ${qrCodeData.clubName}`}
                className="block w-[220px] max-w-full"
              />
            </div>
            <div>
              <p className="text-base font-bold text-cm-text m-0">{qrCodeData.clubName}</p>
              <Badge><span className="font-mono">{qrCodeData.clubCode}</span></Badge>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        title="Club performance"
        description={selectedClub?.name}
        icon={ChartNoAxesCombined}
        size="lg"
        footer={<CmButton variant="secondary" onClick={() => setStatsOpen(false)}>Close</CmButton>}
      >
        {statsLoading || clubStats ? (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <CmStatCard
              label="Top-ups"
              loading={statsLoading}
              value={formatCurrency(clubStats?.stats?.deposits?.totalAmount)}
              hint={`${formatNumber(clubStats?.stats?.deposits?.count)} approved top-ups`}
            />
            <CmStatCard
              label="Unique players"
              loading={statsLoading}
              value={formatNumber(clubStats?.stats?.uniquePlayers)}
              hint={`Balance ${formatCurrency(clubStats?.stats?.totalBalance)}`}
            />
          </div>
        ) : (
          <EmptyState compact icon={ChartNoAxesCombined} title="No statistics yet" description="Numbers appear once the club has transactions." />
        )}
      </Modal>

      <Modal
        open={staffOpen}
        onClose={() => setStaffOpen(false)}
        title="Club staff"
        description={selectedClub?.name}
        icon={Users}
        size="lg"
        footer={<CmButton variant="secondary" onClick={() => setStaffOpen(false)}>Close</CmButton>}
      >
        {staffLoading ? (
          <TableSkeleton rows={3} cols={3} />
        ) : (
          <div className="flex flex-col gap-5">
            {canAssignStaff && canViewAdmins ? (
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <SelectInput
                  className="flex-1"
                  label="Assign an admin"
                  value={selectedAdminId}
                  onChange={(event) => setSelectedAdminId(event.target.value)}
                  placeholder={unassignedAdminOptions.length === 0 ? "No unassigned admins available" : "Select admin"}
                  options={unassignedAdminOptions.map((admin) => ({
                    value: admin._id,
                    label: `${admin.name} · ${admin.email}`,
                  }))}
                  disabled={unassignedAdminOptions.length === 0}
                />
                <CmButton
                  icon={UserPlus}
                  loading={staffActionLoading}
                  disabled={!selectedAdminId}
                  onClick={handleAssignAdmin}
                >
                  Assign
                </CmButton>
              </div>
            ) : (
              <Notice tone="info" icon={Info}>
                Staff assignment follows role permissions. Assigned admins show below when your access allows it.
              </Notice>
            )}

            <div>
              <h3 className="text-sm font-bold text-cm-text m-0 mb-2">
                Assigned admins ({assignedAdmins.length})
              </h3>
              {assignedAdmins.length === 0 ? (
                <EmptyState compact icon={Users} title="No admins assigned" description="Assigned admins can manage this club." />
              ) : (
                <ul className="flex flex-col divide-y divide-cm-border m-0 p-0 list-none rounded-cm-lg border border-cm-border">
                  {assignedAdmins.map((admin) => (
                    <li key={admin._id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <PersonCell name={admin.name} sub={admin.email} />
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge tone="gold">{admin.role?.name || "Unknown"}</Badge>
                        {canAssignStaff && (
                          <CmIconButton
                            icon={Trash2}
                            variant="ghost"
                            size="sm"
                            label={`Remove ${admin.name}`}
                            disabled={staffActionLoading}
                            onClick={() => handleRemoveAdmin(admin._id)}
                          />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal>

      <TableManagement
        clubId={selectedClubForTables?._id}
        open={tableManagerOpen}
        onClose={() => {
          setTableManagerOpen(false);
          setSelectedClubForTables(null);
        }}
      />

      {(loadingVenueDelete || loadingClubDelete) && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-[1400] flex items-center gap-2 rounded-full border border-cm-border bg-cm-surface-3 px-4 py-2 text-sm text-cm-text shadow-cm-lg"
        >
          <Spinner size={14} />
          Updating venue data…
        </div>
      )}
    </div>
  );
};

export default Venues;
